import { useEffect, useState } from "react";
import { Activity, TrendingUp, FileDown, Sparkles } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import api, { fmtNaira } from "@/lib/api";

const GRADE_TONES = {
  A: "from-emerald-500 to-emerald-700 text-white",
  B: "from-lime-500 to-emerald-600 text-white",
  C: "from-amber-400 to-orange-500 text-white",
  D: "from-orange-500 to-rose-500 text-white",
  F: "from-rose-600 to-red-700 text-white",
};

export default function BusinessInsights() {
  const [health, setHealth] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/insights/health-score").then(r => r.data).catch(() => null),
      api.get("/insights/forecast").then(r => r.data).catch(() => null),
    ]).then(([h, f]) => {
      setHealth(h);
      setForecast(f);
    }).finally(() => setLoading(false));
  }, []);

  async function downloadPdf() {
    setDownloading(true);
    try {
      const resp = await api.get("/insights/monthly-report.pdf", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kobolog-monthly-${new Date().toISOString().slice(0, 7)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Couldn't generate the PDF. Try again.");
    } finally {
      setDownloading(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-white border border-border rounded-2xl p-4 text-sm text-kudi-ink/50" data-testid="business-insights-loading">
        Loading business insights…
      </div>
    );
  }

  const tone = GRADE_TONES[health?.grade] || GRADE_TONES.C;
  const chartData = [
    ...(forecast?.history || []).slice(-14).map(h => ({ label: h.weekday, value: h.sales, kind: "actual" })),
    ...(forecast?.forecast || []).map(f => ({ label: f.weekday, value: f.projected_sales, kind: "forecast" })),
  ];
  const historyLen = Math.min((forecast?.history || []).length, 14);

  return (
    <div className="space-y-3" data-testid="business-insights">
      {/* Health score card */}
      {health && (
        <div
          className={`relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br ${tone}`}
          data-testid="health-score-card"
        >
          <div className="absolute inset-0 noise-overlay opacity-30" aria-hidden />
          <div className="relative flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/20">
              <Activity className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider opacity-80">Business health</div>
              <div className="font-heading text-2xl font-bold mt-0.5 tabular-nums">
                {health.score}<span className="text-base opacity-70">/100</span>
                <span className="ml-2 text-base font-semibold bg-white/20 backdrop-blur rounded-full px-2 py-0.5">{health.grade}</span>
              </div>
              <div className="text-xs opacity-90 mt-1 truncate">{health.verdict}</div>
            </div>
          </div>

          <div className="relative mt-3 grid grid-cols-5 gap-1.5">
            {Object.entries(health.subscores).map(([k, v]) => (
              <div key={k} className="bg-white/10 backdrop-blur rounded-lg px-1.5 py-1 text-center border border-white/15" data-testid={`health-sub-${k}`}>
                <div className="text-[10px] uppercase opacity-75 tracking-wide truncate">{k}</div>
                <div className="text-sm font-bold tabular-nums">{Math.round(v)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Forecast card */}
      <div className="bg-white border border-border rounded-2xl p-4" data-testid="forecast-card">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-kudi-green" />
              <div className="font-heading font-semibold">7-day forecast</div>
            </div>
            <div className="text-xs text-kudi-ink/55 mt-0.5">
              Projected sales · confidence:{" "}
              <span className={
                forecast?.confidence === "high" ? "text-emerald-700 font-medium" :
                forecast?.confidence === "medium" ? "text-amber-700 font-medium" :
                "text-kudi-ink/50"
              }>
                {forecast?.confidence || "low"}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-kudi-ink/55">Next 7d</div>
            <div className="font-heading text-lg font-bold tabular-nums text-kudi-green" data-testid="projected-7d">
              {fmtNaira(forecast?.projected_7d || 0)}
            </div>
          </div>
        </div>

        {chartData.length > 0 ? (
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gActual" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#115E3E" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#115E3E" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="gForecast" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D96C4A" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#D96C4A" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" vertical={false} />
                <XAxis dataKey="label" axisLine={false} tickLine={false} fontSize={10} stroke="#5C5C5C" />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #E5E5E0", fontSize: 12 }}
                  formatter={(v, _n, p) => [fmtNaira(v), p?.payload?.kind === "forecast" ? "Projected" : "Actual"]}
                />
                <ReferenceLine x={chartData[historyLen - 1]?.label} stroke="#999" strokeDasharray="3 3" />
                <Area type="monotone" dataKey="value" stroke="#115E3E" fill="url(#gActual)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-xs text-kudi-ink/50 py-4 text-center">
            {forecast?.message || "Log a few days of sales to see a projection."}
          </div>
        )}
      </div>

      {/* Monthly PDF */}
      <button
        onClick={downloadPdf}
        disabled={downloading}
        className="w-full bg-kudi-ink text-white rounded-2xl p-3.5 flex items-center gap-3 hover:bg-kudi-ink/90 active:scale-[0.99] transition disabled:opacity-50"
        data-testid="download-monthly-pdf-btn"
      >
        <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
          {downloading ? <Sparkles className="w-4 h-4 animate-pulse" /> : <FileDown className="w-4 h-4" />}
        </div>
        <div className="flex-1 text-left">
          <div className="font-heading text-sm font-semibold">{downloading ? "Building PDF…" : "Monthly PDF report"}</div>
          <div className="text-[11px] text-white/70 mt-0.5">30-day summary with health score, top customers & debtors</div>
        </div>
      </button>
    </div>
  );
}
